import { isEqualUndefinedOrEmpty, isUndefinedOrEmpty, isEqualUndefined, isUndefined } from '../../../utils/js.js';
import { formatDateFromParts, parseInputDate, parseIsoDateToString, convertDateToISO, parseDateOnlyToString, convertDateToDateOnly, formatDateRange, parseDateRange, parseIsoDateToDate, formatMonth, parseMonthToDate, formatStringTime } from '../../Utils/dateHelper.js';
import { formatValue, ValueFormat, parse, getPrecision } from '../../Utils/numeriHelper.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
const RANGE_SEP = '...';
var InputFormatsEnum;
(function (InputFormatsEnum) {
    InputFormatsEnum["capitalize"] = "capitalize";
    InputFormatsEnum["lowercase"] = "lowercase";
    InputFormatsEnum["uppercase"] = "uppercase";
    InputFormatsEnum["number"] = "number";
    InputFormatsEnum["currency"] = "currency";
    InputFormatsEnum["currencyRange"] = "currencyRange";
    InputFormatsEnum["date"] = "date";
    InputFormatsEnum["dateIso"] = "dateIso";
    InputFormatsEnum["dateOnly"] = "dateOnly";
    InputFormatsEnum["dateRange"] = "dateRange";
    InputFormatsEnum["dateRangeIso"] = "dateRangeIso";
    InputFormatsEnum["month"] = "month";
    InputFormatsEnum["monthString"] = "monthString";
    InputFormatsEnum["time"] = "time";
    InputFormatsEnum["bool"] = "bool";
})(InputFormatsEnum || (InputFormatsEnum = {}));
const InputFormats = InputFormatsEnum;
function getValueFormat(format) {
    if (InputFormatsEnum.currency === format) {
        return ValueFormat.currency;
    }
    return ValueFormat.number;
}
const DefaultInputFormatObjects = {
    /**
     * Formatta il valore di un componente Input
     * @param value valore
     * @param precision precisione
     * @returns ritorna la stringa che rappresenta il valore
     */
    format: (value) => value || '',
    /**
     * Parserizza il valore testuale del componente input ritornando il valore atteso in base al formato.
     * @param value valore del componente input
     * @param precision precisione
     * @returns value valore
     */
    parse: (value) => value,
    /**
     * Confronta due valori.
     */
    equals: (value1, value2) => isEqualUndefinedOrEmpty(value1, value2),
};
function normalizeTextRange(text) {
    return text.replace(/ - +/g, RANGE_SEP).replace(/  +/g, ' ').trim().replace(' ', RANGE_SEP);
}
function capitalizeString(text) {
    return text.toLowerCase().replace(/[ '-]([a-z])/g, (x) => x.toUpperCase()).replace(/^([a-z])/g, (x) => x.toUpperCase());
}
function formatNumber(value, precision = 0) {
    if (typeof value !== 'string') {
        return '';
    }
    if (precision > 0) {
        const trimValue = value.replace(/[^0-9-,.]/g, '');
        if (trimValue.includes('.') && trimValue.includes(',')) {
            // numero in formato 1.000,00, rimuovo solo i punti
            return trimValue.replace('.', '');
        }
        return trimValue.replace('.', ',');
    }
    return value.replace(/[^0-9-]/g, '');
}
function formatNumberRange(value, precision = 0) {
    if (typeof value !== 'string') {
        return '';
    }
    if (precision > 0) {
        return value.replace(/[^0-9-,. ]/g, '').replace('.', ',');
    }
    return value.replace(/[^0-9- ]/g, '');
}
/**
 * Formatta un range di valori in  una stringa.
 * @param values valori
 * @param format proprietà format di Input
 * @returns value valore
 */
function formatCurrencyRange(values, precision = 0) {
    if (!values)
        return undefined;
    return `${formatValue(values[0], ValueFormat.currency, precision)} - ${formatValue(values[1], ValueFormat.currency, precision)}`;
}
/**
 * Parserizza un range di valori.
 * @param value valore del componente input
 * @param format proprietà format di Input
 * @returns value valore
 */
function parseCurrency(value, format, precision) {
    if (typeof value !== 'string' || isUndefinedOrEmpty(value))
        return undefined;
    return Number(parse(value).toFixed(getPrecision(format, precision)));
}
/**
 * Parserizza un range di valori.
 * @param value valore del componente input
 * @param precision precisione
 * @returns value valore
 */
function parseCurrencyRange(value, precision = 0) {
    if (!value)
        return undefined;
    const values = value.split(RANGE_SEP);
    if (values.length !== 2) {
        return undefined;
    }
    return values.map((s) => s.trim()).map((s) => parseCurrency(s, ValueFormat.currency, precision));
}
const inputFormatMap = {
    [InputFormatsEnum.capitalize]: {
        mapTextTransorm: () => InputFormats.capitalize,
        normalizeText: (value) => (typeof value === 'string' ? capitalizeString(value) : ''),
        format: (value) => (typeof value === 'string' ? capitalizeString(value) : ''),
        parse: (value) => (typeof value === 'string' ? capitalizeString(value) : ''),
        equals: isEqualUndefinedOrEmpty,
    },
    [InputFormatsEnum.lowercase]: {
        mapTextTransorm: () => InputFormats.lowercase,
        normalizeText: (value) => (typeof value === 'string' ? value.toLowerCase() : ''),
        format: (value) => (typeof value === 'string' ? value.toLowerCase() : ''),
        parse: (value) => (typeof value === 'string' ? value.toLowerCase() : ''),
        equals: isEqualUndefinedOrEmpty,
    },
    [InputFormatsEnum.uppercase]: {
        mapTextTransorm: () => InputFormats.uppercase,
        normalizeText: (value) => (typeof value === 'string' ? value.toUpperCase() : ''),
        format: (value) => (typeof value === 'string' ? value.toUpperCase() : ''),
        parse: (value) => (typeof value === 'string' ? value.toUpperCase() : ''),
        equals: isEqualUndefinedOrEmpty,
    },
    [InputFormatsEnum.number]: {
        normalizeText: (value, precision) => formatNumber(value, precision),
        format: (value, precision) => (typeof value === 'number' ? formatValue(value, ValueFormat.number, precision) : ''),
        parse: (value, precision) => (typeof value === 'string' && !isUndefinedOrEmpty(value)
            ? Number(parse(value).toFixed(getPrecision(ValueFormat.number, precision)))
            : undefined),
        equals: isEqualUndefined,
    },
    [InputFormatsEnum.currency]: {
        normalizeText: (value, precision) => formatNumber(value, getPrecision(ValueFormat.currency, precision)),
        format: (value, precision) => (typeof value === 'number'
            ? formatValue(value, ValueFormat.currency, getPrecision(ValueFormat.currency, precision))
            : ''),
        parse: (value, precision) => (typeof value === 'string' && !isUndefinedOrEmpty(value)
            ? Number(parse(value).toFixed(getPrecision(ValueFormat.currency, precision)))
            : undefined),
        equals: isEqualUndefined,
    },
    [InputFormatsEnum.currencyRange]: {
        normalizeText: (value, precision) => formatNumberRange(value, precision),
        format: (value, precision) => (Array.isArray(value) ? formatCurrencyRange(value, precision) : value),
        parse: (value, precision) => (typeof value === 'string' ? parseCurrencyRange(normalizeTextRange(value), precision) : value),
        equals: (value1, value2) => (isUndefined(value1) && isUndefined(value2))
            || (!isUndefined(value1) && !isUndefined(value2) && value1[0] === value2[0] && value1[1] === value2[1]),
    },
    [InputFormatsEnum.date]: {
        normalizeText: (value) => (typeof value === 'string' ? value.replace(/[^0-9/]/g, '') : ''),
        format: (value) => (value instanceof Date ? formatDateFromParts(value.getDate(), value.getMonth() + 1, value.getFullYear(), '/') : value),
        parse: (value) => (typeof value === 'string' ? parseInputDate(value) : value),
        equals: (value1, value2) => (isUndefined(value1) && isUndefined(value2))
            || (!isUndefined(value1) && !isUndefined(value2) && value1.getTime() === value2.getTime()),
    },
    [InputFormatsEnum.dateIso]: {
        normalizeText: (value) => (typeof value === 'string' ? value.replace(/[^0-9/]/g, '') : ''),
        format: (value) => (value ? parseIsoDateToString(convertDateToISO(new Date(value))) : ''),
        parse: (value) => {
            const date = parseInputDate(value);
            if (date) {
                return convertDateToISO(date);
            }
            return undefined;
        },
        equals: isEqualUndefinedOrEmpty,
    },
    [InputFormatsEnum.dateOnly]: {
        normalizeText: (value) => (typeof value === 'string' ? value.replace(/[^0-9/]/g, '') : ''),
        format: (value) => (value ? parseDateOnlyToString(convertDateToDateOnly(new Date(value))) : ''),
        parse: (value) => {
            const date = parseInputDate(value);
            if (date) {
                return convertDateToDateOnly(date);
            }
            return undefined;
        },
        equals: isEqualUndefinedOrEmpty,
    },
    [InputFormatsEnum.dateRange]: {
        normalizeText: (value) => (typeof value === 'string' ? value.replace(/[^0-9 /-\s]/g, '') : ''),
        format: (value) => (Array.isArray(value) ? formatDateRange(value[0], value[1]) : value),
        parse: (value) => (typeof value === 'string' ? parseDateRange(normalizeTextRange(value), true, true, undefined, RANGE_SEP) : value),
        equals: (value1, value2) => (isUndefined(value1) && isUndefined(value2))
            || (!isUndefined(value1) && !isUndefined(value2) && value1[0].getTime() === value2[0].getTime() && value1[1].getTime() === value2[1].getTime()),
    },
    [InputFormatsEnum.dateRangeIso]: {
        normalizeText: (value) => (typeof value === 'string' ? value.replace(/[^0-9 /-\s]/g, '') : ''),
        format: (value) => (Array.isArray(value) ? formatDateRange(parseIsoDateToDate(value[0]), parseIsoDateToDate(value[1])) : ''),
        parse: (value) => (typeof value === 'string'
            ? parseDateRange(normalizeTextRange(value), true, true, undefined, RANGE_SEP).map(convertDateToISO) : [undefined, undefined]),
        equals: (value1, value2) => (isUndefined(value1) && isUndefined(value2))
            || (!isUndefined(value1) && !isUndefined(value2) && isEqualUndefinedOrEmpty(value1[0], value2[0]) && isEqualUndefinedOrEmpty(value1[1], value2[1])),
    },
    [InputFormatsEnum.month]: {
        normalizeText: (value) => (typeof value === 'string' ? value.replace(/[^0-9/]/g, '') : ''),
        format: (value) => (value instanceof Date ? formatMonth(value.getMonth() + 1, value.getFullYear(), '/') : value),
        parse: (value) => (typeof value === 'string' ? parseMonthToDate(value, true, true) : value),
    },
    [InputFormatsEnum.monthString]: {
        normalizeText: (value) => (typeof value === 'string' ? value.replace(/[^0-9/]/g, '') : ''),
        format: (value) => value,
        parse: (value) => (parseMonthToDate(value, true, true) === null ? '' : value),
        equals: isEqualUndefinedOrEmpty,
    },
    [InputFormatsEnum.time]: {
        normalizeText: (value) => (typeof value === 'string' ? value.replace(/[^0-9:]/g, '') : ''),
        format: (value) => value,
        parse: (value) => (typeof value === 'string' ? formatStringTime(value) : value),
        equals: isEqualUndefinedOrEmpty,
    },
    [InputFormatsEnum.bool]: {
        format: (value) => (typeof value === 'boolean' ? `${value}` : ''),
        parse: (value) => (typeof value === 'string' && !isUndefinedOrEmpty(value)
            ? value === `${true}`
            : undefined),
        equals: isEqualUndefined,
    },
};
/**
 * Dato un formato, ritorna l'oggetto associato per formattazione/parserizzazione del testo.
 * @param format formato nei tipi codificati o in oggetto
 */
function getInputFormat(format) {
    if (typeof format === 'string') {
        return inputFormatMap[format];
    }
    if (typeof format === 'object' && format !== null) {
        return Object.assign(Object.assign({}, DefaultInputFormatObjects), format);
    }
    return DefaultInputFormatObjects;
}
function createFocus(ref) {
    return () => {
        if (ref.current) {
            ref.current.focus();
        }
    };
}
function createContains(ref) {
    return (other) => {
        if (ref.current) {
            return ref.current.contains(other);
        }
        return false;
    };
}

export { InputFormats, InputFormatsEnum, capitalizeString, createContains, createFocus, getInputFormat, getValueFormat };
